import { CategoryTree } from '@/components/category-tree';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { type FilterOptions, type ListingFilters } from '@/types';
import { ChevronDown, ChevronRight, ChevronUp, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface AppliedFilter {
    type: keyof ListingFilters;
    value: string | number;
    display: string;
}

type SectionKey = 'category' | 'location';

const MOBILE_COLLAPSED_SECTIONS: Record<SectionKey, boolean> = {
    category: false,
    location: false,
};

const DESKTOP_DEFAULT_SECTIONS: Record<SectionKey, boolean> = {
    category: true,
    location: true,
};

const MOBILE_BREAKPOINT = 1024;

const getInitialExpandedState = () => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
        return MOBILE_COLLAPSED_SECTIONS;
    }

    return DESKTOP_DEFAULT_SECTIONS;
};

const stateLabel = (name: string, code: string) => `${name}, ${code}`;

interface ListingFilterSidebarProps {
    filters: ListingFilters;
    onFiltersChange: (filters: ListingFilters) => void;
    onSaveSearch: () => void;
    filterOptions: FilterOptions;
    className?: string;
}

export const ListingFilterSidebar: React.FC<ListingFilterSidebarProps> = ({
    filters,
    onFiltersChange,
    onSaveSearch,
    filterOptions,
    className = '',
}) => {
    const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>(getInitialExpandedState);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

    const categoryLookup = useMemo(() => {
        const lookup: Record<number, string> = {};

        for (const category of filterOptions.categories) {
            lookup[category.id] = category.name;
        }

        return lookup;
    }, [filterOptions.categories]);

    const stateLookup = useMemo(() => {
        const lookup: Record<string, string> = {};

        for (const state of filterOptions.states) {
            lookup[state.slug ?? state.id] = stateLabel(state.name, state.code);
        }

        return lookup;
    }, [filterOptions.states]);

    const hasActiveFilters = filters.category.length > 0 || filters.location.length > 0;

    // Update state when window resizes (only for mobile to desktop transition)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= MOBILE_BREAKPOINT) {
                setExpandedSections(DESKTOP_DEFAULT_SECTIONS);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSection = (section: SectionKey) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    const clearAllFilters = () => {
        onFiltersChange({
            category: [],
            location: [],
        });
    };

    const removeFilter = (filter: AppliedFilter) => {
        const newFilters = { ...filters };

        switch (filter.type) {
            case 'category':
                newFilters.category = newFilters.category.filter((c) => c !== String(filter.value));
                break;
            case 'location':
                newFilters.location = newFilters.location.filter((slug) => slug !== String(filter.value));
                break;
        }

        onFiltersChange(newFilters);
    };

    const appliedFilters = useMemo<AppliedFilter[]>(() => {
        const nextFilters: AppliedFilter[] = [];

        for (const categoryId of filters.category) {
            nextFilters.push({
                type: 'category',
                value: categoryId,
                display: categoryLookup[Number(categoryId)] || `Categoria ${categoryId}`,
            });
        }

        for (const slug of filters.location) {
            nextFilters.push({
                type: 'location',
                value: slug,
                display: stateLookup[slug] || `Estado ${slug}`,
            });
        }

        return nextFilters;
    }, [categoryLookup, filters.category, filters.location, stateLookup]);

    const FilterSection: React.FC<{
        title: string;
        sectionKey: SectionKey;
        children: React.ReactNode;
    }> = ({ title, sectionKey, children }) => (
        <div className="mb-4 border-b border-gray-200 pb-4 last:mb-0 last:border-b-0 last:pb-0">
            <button
                onClick={() => toggleSection(sectionKey)}
                className="flex w-full items-center justify-between py-2 text-left font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
                {title}
                {expandedSections[sectionKey] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {expandedSections[sectionKey] && <div className="mt-3 space-y-3">{children}</div>}
        </div>
    );

    return (
        <div className={cn('space-y-6 rounded-xl bg-white px-2 lg:p-6', className)}>
            {/* Mobile Toggle Button - Only visible on mobile */}
            <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="flex w-full items-center justify-between py-2 text-left font-semibold text-gray-900 transition-colors hover:text-gray-700 lg:hidden"
            >
                <span>Filtros {appliedFilters.length > 0 && `(${appliedFilters.length})`}</span>
                {isSidebarCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
            </button>

            {/* Collapsible content - hidden on mobile when collapsed, always visible on desktop */}
            <div className={`space-y-6 ${isSidebarCollapsed ? 'hidden lg:block' : 'block'}`}>
                {/* Applied Filters Section */}
                {appliedFilters.length > 0 && (
                    <div>
                        <h3 className="mb-3 font-semibold text-gray-900">Filtros aplicados</h3>
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-2">
                                {appliedFilters.map((filter) => (
                                    <Badge
                                        key={`${filter.type}-${filter.value}`}
                                        className="border border-blue-800 bg-white text-blue-800 hover:bg-blue-50"
                                    >
                                        {filter.display}
                                        <button
                                            onClick={() => removeFilter(filter)}
                                            className="ml-2 hover:text-blue-900"
                                            aria-label={`Remover filtro ${filter.display}`}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={clearAllFilters} className="text-xs" disabled={!hasActiveFilters}>
                                    Limpar todos
                                </Button>
                                <Button size="sm" onClick={onSaveSearch} className="bg-green-600 text-xs hover:bg-green-700">
                                    Salvar pesquisa
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filter Categories */}
                <div className="space-y-1">
                    <FilterSection title="Categoria" sectionKey="category">
                        <CategoryTree
                            categories={filterOptions.categories}
                            selectedCategories={filters.category}
                            onCategoryChange={(categoryId, checked) => {
                                const newCategories = checked
                                    ? Array.from(new Set([...filters.category, categoryId]))
                                    : filters.category.filter((c) => c !== categoryId);
                                onFiltersChange({ ...filters, category: newCategories });
                            }}
                        />
                    </FilterSection>

                    <FilterSection title="Localização" sectionKey="location">
                        <div className="space-y-2">
                            {filterOptions.states.map((state) => {
                                const slug = state.slug ?? String(state.id);
                                const isChecked = filters.location.includes(slug);
                                const isDisabled = state.count === 0 && !isChecked;

                                return (
                                    <label
                                        key={state.id}
                                        className={cn('flex items-center space-x-2', isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                                    >
                                        <Checkbox
                                            checked={isChecked}
                                            disabled={isDisabled}
                                            onCheckedChange={(checked) => {
                                                const shouldAdd = checked === true;
                                                const newLocations = shouldAdd
                                                    ? [...filters.location, slug]
                                                    : filters.location.filter((s) => s !== slug);

                                                onFiltersChange({
                                                    ...filters,
                                                    location: newLocations,
                                                });
                                            }}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {stateLabel(state.name, state.code)}
                                            {state.count !== undefined && <span className="ml-1 text-gray-500">({state.count})</span>}
                                        </span>
                                    </label>
                                );
                            })}
                            {filterOptions.states.length === 0 && <p className="text-sm text-gray-500 italic">Nenhum estado disponível</p>}
                        </div>
                    </FilterSection>
                </div>
            </div>
        </div>
    );
};
